import {globalError} from 'modules/actions';
import {getSentryUser, getMyLeagues, getLeaguesToJoin} from 'modules/selectors';
import Api from 'modules/utils/Api';
import ApiError from 'modules/utils/Api/ApiError';
import {captureSentryError} from 'modules/utils/sentry';
import {call, put, select} from 'typed-redux-saga';

import {
	createLeagueSuccess,
	createLeagueFailed,
	fetchMyLeaguesSuccess,
	fetchMyLeaguesFailed,
	fetchLeaguesToJoinSuccess,
	fetchLeaguesToJoinFailed,
	joinToLeagueSuccess,
	joinToLeagueFailed,
	fetchLeagueByIdSuccess,
	fetchLeagueByIdFailed,
	updateLeagueSuccess,
	updateLeagueFailed,
	deleteLeagueFailed,
	leaveLeagueSuccess,
	leaveLeagueFailed,
	fetchLeagueUsersSuccess,
	fetchLeagueUsersFailed,
	removeLeagueUserSuccess,
	removeLeagueUserFailed,
	deleteLeagueSuccess,

	type createLeague,
	type fetchMyLeagues,
	type fetchLeaguesToJoin,
	type joinToLeague,
	type fetchLeagueById,
	type updateLeague,
	type deleteLeague,
	type leaveLeague,
	type fetchLeagueUsers,
	type removeLeagueUser
} from 'modules/actions/leagues';
import {createFanHubEvent, EventGUID} from 'modules/utils/analytics/analytics';

export const createLeagueSaga = function* (action: ReturnType<typeof createLeague>) {
	try {
		const response = yield* call(Api.Leagues.create, action.payload);

		ApiError.CHECK(response.data);

		createFanHubEvent(EventGUID.CREATE_LEAGUES_COMPLETE)
		yield* put(createLeagueSuccess(response.data.success));
	} catch (e: unknown) {
		const error = e as Error;
		const user = yield* select(getSentryUser);
		if (!error?.message.includes("league")) {
			captureSentryError("createLeagueSaga", "none", e, user);
			yield* put(globalError(e));
		}

		yield* put(createLeagueFailed(e as Error));
	}
};

export const updateLeagueSaga = function* (action: ReturnType<typeof updateLeague>) {
	try {
		const response = yield* call(Api.Leagues.update, action.payload);

		ApiError.CHECK(response.data);

		yield* put(updateLeagueSuccess(response.data.success));
	} catch (e: unknown) {
		const error = e as Error;
		const user = yield* select(getSentryUser);
		const nameUniqError = "league.name.not_unique";
		if (error?.message !== nameUniqError) {
			captureSentryError("updateLeagueSaga", "none", e, user);
			yield* put(globalError(e));
		}

		yield* put(updateLeagueFailed(e as Error));
	}
};

export const fetchMyLeaguesSaga = function* (action: ReturnType<typeof fetchMyLeagues>) {
	try {
		const response = yield* call(Api.Leagues.my_leagues, {
			limit: 20,
			...action.payload,
		});

		ApiError.CHECK(response.data);

		const isFirstPage = action.payload.page === 1;

		const leaguesInStore = yield* select(getMyLeagues);

		const leaguesArray = isFirstPage
			? response.data.success.leagues
			: [...leaguesInStore.leagues, ...response.data.success.leagues];

		yield* put(
			fetchMyLeaguesSuccess({
				...response.data.success,
				leagues: leaguesArray,
			})
		);
	} catch (e) {
		const user = yield* select(getSentryUser);
		captureSentryError("fetchMyLeaguesSaga", "none", e, user);
		yield* put(globalError(e));
		yield* put(fetchMyLeaguesFailed(e as Error));
	}
};

export const fetchLeaguesToJoinSaga = function* (action: ReturnType<typeof fetchLeaguesToJoin>) {
	try {
		const response = yield* call(Api.Leagues.leagues_to_join, action.payload);

		ApiError.CHECK(response.data);

		const leaguesInStore = yield* select(getLeaguesToJoin);

		const isFirstPage = action.payload.page === 1;

		const leaguesArray = isFirstPage
			? response.data.success.leagues
			: [...leaguesInStore.leagues, ...response.data.success.leagues];

		yield* put(
			fetchLeaguesToJoinSuccess({
				...response.data.success,
				leagues: leaguesArray,
			})
		);
	} catch (e) {
		const user = yield* select(getSentryUser);
		captureSentryError("fetchLeaguesToJoin", "none", e, user);
		yield* put(globalError(e));
		yield* put(fetchLeaguesToJoinFailed());
	}
};

export const joinToLeagueSaga = function* (action: ReturnType<typeof joinToLeague>) {
	try {
		const response = yield* call(Api.Leagues.join_league, action.payload);

		ApiError.CHECK(response.data);
		yield* put(joinToLeagueSuccess(response.data.success));
		createFanHubEvent(EventGUID.JOIN_LEAGUES_COMPLETE)
		action.payload.onSuccess?.();
	} catch (e) {
		const user = yield* select(getSentryUser);
		captureSentryError("joinToLeagueSaga", "none", e, user);
		yield* put(globalError(e));
		yield* put(joinToLeagueFailed());
		action.payload.onError?.();
	}
};

export const fetchLeagueByIdSaga = function* (action: ReturnType<typeof fetchLeagueById>) {
	try {
		const response = yield* call(Api.Leagues.league_by_id, action.payload);
		ApiError.CHECK(response.data);
		yield* put(fetchLeagueByIdSuccess(response.data.success));
	} catch (e) {
		const user = yield* select(getSentryUser);
		captureSentryError("fetchLeagueByIdSaga", "none", e, user);
		yield* put(globalError(e));
		yield* put(fetchLeagueByIdFailed());
	}
};

export const deleteLeagueSaga = function* (action: ReturnType<typeof deleteLeague>) {
	try {
		const {onSuccess, ...payload} = action.payload;
		const response = yield* call(Api.Leagues.delete, payload);

		ApiError.CHECK(response.data);
		yield* put(deleteLeagueSuccess());

		onSuccess();
	} catch (e) {
		const user = yield* select(getSentryUser);
		captureSentryError("deleteLeagueSaga", "none", e, user);
		yield* put(globalError(e));
		yield* put(deleteLeagueFailed(e as Error));
	}
};

export const leaveLeagueSaga = function* (action: ReturnType<typeof leaveLeague>) {
	try {
		const {onSuccess, ...payload} = action.payload;
		const response = yield* call(Api.Leagues.leave, payload);

		ApiError.CHECK(response.data);
		yield* put(leaveLeagueSuccess());

		onSuccess();
	} catch (e) {
		const user = yield* select(getSentryUser);
		captureSentryError("leaveLeagueSaga", "none", e, user);
		yield* put(globalError(e));
		yield* put(leaveLeagueFailed(e as Error));
	}
};

export const fetchLeagueUsersSaga = function* ({payload}: ReturnType<typeof fetchLeagueUsers>) {
	try {
		const response = yield* call(Api.Leagues.league_users, payload);

		ApiError.CHECK(response.data);

		yield* put(fetchLeagueUsersSuccess(response.data.success));
	} catch (e) {
		const user = yield* select(getSentryUser);
		captureSentryError("fetchLeagueUsersSaga", "none", e, user);
		yield* put(globalError(e));
		yield* put(fetchLeagueUsersFailed(e as Error));
	}
};

export const removeLeagueUserSaga = function* ({payload}: ReturnType<typeof removeLeagueUser>) {
	try {
		const response = yield* call(Api.Leagues.remove_user, payload);

		ApiError.CHECK(response.data);

		yield* put(removeLeagueUserSuccess({userId: payload.userId}));
	} catch (e) {
		const user = yield* select(getSentryUser);
		captureSentryError("removeLeagueUserSaga", "none", e, user);
		yield* put(globalError(e));
		yield* put(removeLeagueUserFailed(e as Error));
	}
};
